import Button from '@atlaskit/button';
import { ErrorMessage } from '@atlaskit/form';
import AddIcon from '@atlaskit/icon/glyph/add';
import Textfield from '@atlaskit/textfield';
import Toggle from '@atlaskit/toggle';
import Tooltip from '@atlaskit/tooltip';
import React, { useContext, useState } from 'react';
import styled from '@emotion/styled';
import { GlobalContext } from '../../common/GlobalContext';
import { OptionListColumnDimensions } from './FieldEditor';
import { FieldEditorActionReturnType } from '../../stateful/field-editor-stateful/FieldEditorStateful';
import { DTOCustomFieldOption } from '../../../generated-common';
import { token } from '@atlaskit/tokens';

export const TdNewInner = styled.div`
  display: flex;
  width: 100%;
`;
export const TdNewTextfieldWrapper = styled.div`
  flex-grow: 1;
  margin-left: -8px;
  padding-right: 20px;
`;

export const FieldEditorCreateNewOptionRow = ({
  onCreatedOption,
  columnDimensions,
  hasChildOptions,
  scrollTopBottom,
  rightTopComponent,
}: {
  onCreatedOption: (option: DTOCustomFieldOption) => Promise<FieldEditorActionReturnType>;
  columnDimensions: OptionListColumnDimensions;
  hasChildOptions: boolean;
  scrollTopBottom: () => void;
  rightTopComponent: React.ReactNode;
}) => {
  const { t } = useContext(GlobalContext);
  const [createOptionLoading, setCreateOptionLoading] = useState<boolean>(false);
  const [createOptionValue, setCreateOptionValue] = useState<string>('');
  const [createOptionDisabled, setCreateOptionDisabled] = useState<boolean>(false);
  const [createOptionHasError, setCreateOptionHasError] = useState<boolean>(false);
  const [createOptionError, setCreateOptionError] = useState<string | null>(null);

  const handleOnChange = (e: any) => {
    setCreateOptionValue(e.target.value);
  };
  const handleOnBlur = () => {};
  const handleOnFocus = () => {};

  const doCreateOption = () => {
    setCreateOptionLoading(true);
    onCreatedOption({ optionId: undefined, id: '-1', value: createOptionValue ? createOptionValue : '', disabled: createOptionDisabled }).then((createdResult: any) => {
      if (createdResult && createdResult.errors) {
        setCreateOptionHasError(true);
        if (createdResult.errors && createdResult.errors.length > 0 && createdResult.errors[0].message) {
          setCreateOptionError(createdResult.errors[0].message);
        }
        setCreateOptionLoading(false);
        // console.log('onUpdateOption - create - ERROR');
      } else {
        setCreateOptionHasError(false);
        setCreateOptionError('');
        setCreateOptionValue('');
        setCreateOptionLoading(false);
        scrollTopBottom();
        // console.log('onUpdateOption - create - SUCCESS');
      }
    });
  };
  return (
    <div style={{ display: 'flex', paddingBottom: 4, backgroundColor: token('elevation.surface', '#fff') }}>
      <div style={{ width: columnDimensions.positionPx, maxWidth: columnDimensions.positionPx, minWidth: columnDimensions.positionPx }}>&nbsp;</div>
      <div style={{ width: columnDimensions.activePx, maxWidth: columnDimensions.activePx, minWidth: columnDimensions.activePx, paddingTop: 6 }}>
        <span data-e2e-create-disabled="true" data-e2e-create-disabled-value={!createOptionDisabled}>
          <Toggle isChecked={!createOptionDisabled} onChange={() => setCreateOptionDisabled((prev) => !prev)} />
        </span>
      </div>
      <div style={{ flex: 1 }}>
        <TdNewInner>
          <TdNewTextfieldWrapper>
            <Textfield
              placeholder={t('fieldEditor.newOption.placeholder')}
              data-e2e-create-optionvalue="true"
              value={createOptionValue}
              isInvalid={createOptionHasError}
              onChange={handleOnChange}
              onBlur={handleOnBlur}
              onFocus={handleOnFocus}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter') {
                  doCreateOption();
                } else {
                  if (createOptionHasError === true) {
                    setCreateOptionHasError(false);
                    setCreateOptionError('');
                  }
                }
              }}
              elemAfterInput={
                <div style={{ marginRight: '2px', marginTop: '1px', width: '30px' }}>
                  <Tooltip content={t('fieldEditor.newOption.addButton')}>
                    <span data-e2e-create-button="true">
                      <Button
                        isDisabled={createOptionLoading}
                        appearance="subtle"
                        onClick={() => doCreateOption()}
                        iconBefore={<AddIcon size="small" label="add" primaryColor={token('color.text.accent.blue.bolder', '#253858')} />}
                      />
                    </span>
                  </Tooltip>
                </div>
              }
            />
            {createOptionHasError === true && (
              <div data-e2e-create-optionvalue-error="true">
                <ErrorMessage>{createOptionError}</ErrorMessage>
              </div>
            )}
          </TdNewTextfieldWrapper>
        </TdNewInner>
      </div>
      <div style={{ width: columnDimensions.defaultPx, maxWidth: columnDimensions.defaultPx, minWidth: columnDimensions.defaultPx }}>&nbsp;</div>
      {hasChildOptions && <div style={{ width: columnDimensions.childPx, maxWidth: columnDimensions.childPx, minWidth: columnDimensions.childPx }}>&nbsp;</div>}
      <div style={{ width: columnDimensions.deletePx, maxWidth: columnDimensions.deletePx, minWidth: columnDimensions.deletePx }}>{rightTopComponent ? rightTopComponent : <> &nbsp;</>}</div>
    </div>
  );
};
